import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { RefreshCw, Copy, Save, TrendingUp, TrendingDown, AlertTriangle, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [[7, '7 Tage'], [28, '28 Tage'], [90, '3 Monate']];
const de = (n, digits = 0) => (n || 0).toLocaleString('de-DE', { maximumFractionDigits: digits });
const pct = (n) => `${de((n || 0) * 100, 1)} %`;
const pos = (n) => (n ? de(n, 1) : '–');
const day = (d) => new Date(d).toLocaleDateString('de-DE');
const pagePath = (url) => { try { return new URL(url).pathname; } catch { return url; } };
const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success('Kopiert'));

const STEPS = [
  <>In der <a className="text-[#066ce7] hover:underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">Google Cloud Console</a> ein Projekt anlegen und die „Google Search Console API“ aktivieren.</>,
  <>Unter „IAM & Verwaltung → Dienstkonten“ ein Dienstkonto anlegen und einen Schlüssel im JSON-Format herunterladen.</>,
  <>In Railway die Variable <span className="font-mono text-xs bg-slate-100 px-1 rounded">GSC_SERVICE_ACCOUNT_JSON</span> anlegen, den kompletten Inhalt der JSON-Datei einfügen und neu deployen.</>,
  <>In der Search Console unter „Einstellungen → Nutzer und Berechtigungen“ die Dienstkonto-E-Mail (endet auf …iam.gserviceaccount.com) mit Berechtigung „Eingeschränkt“ hinzufügen.</>,
  <>Unten die Property genau so eintragen, wie sie in der Search Console heißt.</>,
];

function Delta({ now, before, lowerIsBetter }) {
  if (!before) return <span className="text-xs text-slate-400">kein Vergleichswert</span>;
  const change = (now - before) / before;
  const good = lowerIsBetter ? change < 0 : change > 0;
  const Icon = change >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${change === 0 ? 'text-slate-400' : good ? 'text-emerald-600' : 'text-red-500'}`}>
      <Icon className="w-3.5 h-3.5" /> {change > 0 ? '+' : ''}{de(change * 100)} % ggü. Vorzeitraum
    </span>
  );
}

function Kpi({ label, value, delta, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 p-5">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-3xl font-extrabold text-[#0F172A] mt-2" data-testid={testid}>{value}</p>
      <div className="mt-1.5">{delta}</div>
    </div>
  );
}

function DailyChart({ daily }) {
  return (
    <div className="h-64" data-testid="gsc-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={daily} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="date" tickFormatter={d => `${d.slice(8, 10)}.${d.slice(5, 7)}.`} tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={24} />
          <YAxis yAxisId="c" allowDecimals={false} tick={{ fontSize: 11, fill: '#066ce7' }} />
          <YAxis yAxisId="i" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
          <Tooltip labelFormatter={day} formatter={(v, name) => [de(v), name]} />
          <Area yAxisId="i" type="monotone" dataKey="impressions" name="Impressionen" stroke="#94a3b8" fill="#e2e8f0" strokeWidth={1.5} />
          <Area yAxisId="c" type="monotone" dataKey="clicks" name="Klicks" stroke="#066ce7" fill="#066ce7" fillOpacity={0.15} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function TopTable({ title, keyLabel, rows, format = (k) => k, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <h3 className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-[#0F172A]">{title}</h3>
      {!rows.length ? <p className="p-4 text-sm text-slate-500">Noch keine Daten in diesem Zeitraum.</p> : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr><th className="text-left font-medium px-4 py-2">{keyLabel}</th><th className="text-right font-medium px-2">Klicks</th><th className="text-right font-medium px-2">Impr.</th><th className="text-right font-medium px-4">Pos.</th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map(r => (
              <tr key={r.key} className="border-t border-slate-100">
                <td className="px-4 py-2 max-w-[240px] truncate text-[#0F172A]" title={r.key}>{format(r.key)}</td>
                <td className="px-2 py-2 text-right font-semibold">{de(r.clicks)}</td>
                <td className="px-2 py-2 text-right text-slate-500">{de(r.impressions)}</td>
                <td className="px-4 py-2 text-right text-slate-500">{pos(r.position)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Report({ report }) {
  const { totals: now, previous: before } = report;
  return (
    <div className="space-y-6" data-testid="gsc-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Klicks" value={de(now.clicks)} delta={<Delta now={now.clicks} before={before.clicks} />} testid="gsc-clicks" />
        <Kpi label="Impressionen" value={de(now.impressions)} delta={<Delta now={now.impressions} before={before.impressions} />} testid="gsc-impressions" />
        <Kpi label="Klickrate (CTR)" value={pct(now.ctr)} delta={<Delta now={now.ctr} before={before.ctr} />} testid="gsc-ctr" />
        <Kpi label="Ø Position" value={pos(now.position)} delta={<Delta now={now.position} before={before.position} lowerIsBetter />} testid="gsc-position" />
      </div>
      <DailyChart daily={report.daily} />
      <div className="grid lg:grid-cols-2 gap-6">
        <TopTable title="Top-Suchbegriffe" keyLabel="Suchbegriff" rows={report.queries} testid="gsc-queries" />
        <TopTable title="Top-Seiten" keyLabel="Seite" rows={report.pages} format={pagePath} testid="gsc-pages" />
      </div>
      <p className="text-xs text-slate-400">Zeitraum {day(report.start)} – {day(report.end)} · Google liefert Daten mit 2–3 Tagen Verzögerung · Abruf {new Date(report.fetched_at * 1000).toLocaleTimeString('de-DE')}</p>
    </div>
  );
}

function Setup({ status, headers, onSaved }) {
  const [property, setProperty] = useState(status.property);
  useEffect(() => setProperty(status.property), [status.property]);
  const save = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { key: 'gsc_property', value: property.trim() }, { headers });
      toast.success('Property gespeichert');
      onSaved();
    } catch { toast.error('Fehler beim Speichern'); }
  };
  return (
    <div className="space-y-5 md:pl-[52px]" data-testid="gsc-setup">
      {!status.configured && (
        <>
          <p className="text-sm text-slate-600" data-testid="gsc-not-configured">Noch nicht verbunden. Einmalig einrichten (ca. 15 Minuten), danach erscheinen Klicks und Positionen automatisch hier:</p>
          <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">{STEPS.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </>
      )}
      {status.service_account_email && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="gsc-service-account">
          <span className="text-slate-500">Dienstkonto (in der Search Console als Nutzer hinzufügen):</span>
          <span className="font-mono text-xs bg-slate-100 rounded px-2 py-1">{status.service_account_email}</span>
          <Button variant="outline" size="sm" onClick={() => copy(status.service_account_email)} data-testid="gsc-copy-email"><Copy className="w-3.5 h-3.5" /></Button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="flex-1">
          <Label className="text-xs">Search-Console-Property</Label>
          <Input value={property} onChange={e => setProperty(e.target.value)} placeholder="https://www.itcs-global.com/ oder sc-domain:itcs-global.com" data-testid="gsc-property-input" />
        </div>
        <Button onClick={save} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="gsc-property-save"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
      </div>
    </div>
  );
}

export function SearchConsolePanel({ headers }) {
  const [days, setDays] = useState(28);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((refresh = false) => {
    setLoading(true);
    axios.get(`${API}/admin/gsc`, { headers, params: { days, refresh } })
      .then(r => setStatus(r.data))
      .catch(() => toast.error('Search-Console-Daten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [days, headers]);
  useEffect(() => { load(); }, [load]);

  const report = status?.report;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-testid="gsc-panel">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#066ce7] flex items-center justify-center shrink-0"><BarChart3 className="w-5 h-5" /></span>
          <div>
            <h2 className="text-base font-semibold text-[#0F172A]">Google-Ergebnisse (Search Console)</h2>
            <p className="text-sm text-slate-500">Echte Klicks, Impressionen und Positionen aus der Google-Suche{status?.property ? ` – ${status.property}` : ''}.</p>
          </div>
        </div>
        {report && (
          <div className="flex items-center gap-1" data-testid="gsc-periods">
            {PERIODS.map(([value, label]) => (
              <button key={value} onClick={() => setDays(value)} data-testid={`gsc-period-${value}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${days === value ? 'bg-[#066ce7] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{label}</button>
            ))}
            <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} className="ml-2" data-testid="gsc-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        )}
      </div>
      {!status && <p className="text-sm text-slate-500">Lade…</p>}
      {status?.error && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="gsc-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {status.error}
        </div>
      )}
      {status && !report && <Setup status={status} headers={headers} onSaved={() => load(true)} />}
      {report && <Report report={report} />}
    </div>
  );
}
